import { ApiFormError } from '@trade-platform/ui-utils';
import { v4 as uuid } from 'uuid';
import { ErrorWrapper } from '../store/models/common';
import { AnalyticsEvents } from './analytics-events';
import { AnalyticsTracker } from './analytics-tracker';
import { orderEventProperties, toAnalyticsId } from './order-analytics';

type PurchaseStartFlow = AnalyticsEvents['purchase_start_opened']['flow'];

/** A start page's ids from its `productId` route parameter and `shareClass` query parameter. */
export function orderStartIds(productId: string | undefined, shareClassId: string | undefined) {
    return { product_family_id: toAnalyticsId(productId), fund_id: toAnalyticsId(shareClassId) };
}

/**
 * The purchase-start funnel for one start page: `purchase_start_opened` once the page settles on
 * its product, `purchase_start_failed` for each rejected create, then `purchase_start_completed`
 * when the order is created or `purchase_start_abandoned` if the page closes without one. Nothing
 * is sent for a page that only redirected elsewhere. Every event carries the same attempt id.
 */
export class PurchaseStartAnalytics {
    /** One per page visit, so an attempt's events can be counted and joined to the order it made. */
    private readonly attemptId = uuid();
    private ids?: ReturnType<typeof orderStartIds>;
    private submitAttempted = false;
    private orderCreated = false;

    constructor(
        private readonly tracker: AnalyticsTracker,
        private readonly flow: PurchaseStartFlow
    ) {}

    /** The page settled on a product; picking another share class only updates the ids. */
    opened(productId: string | undefined, shareClassId: string | undefined): void {
        const isFirst = !this.ids;
        this.ids = orderStartIds(productId, shareClassId);
        if (isFirst) {
            this.tracker.track('purchase_start_opened', this.properties());
        }
    }

    submitted(): void {
        this.submitAttempted = true;
    }

    /** The order exists now; its firm makes it count under that firm, like the server's events. */
    created(order: { id: string; firm?: { id: string } }): void {
        this.orderCreated = true;
        this.tracker.track('purchase_start_completed', {
            ...this.properties(),
            ...orderEventProperties(order, 'buy')
        });
    }

    failed(error: ErrorWrapper | ApiFormError): void {
        const isValidation = error instanceof ApiFormError;
        const codes = isValidation
            ? (error.messages ?? []).map(item => item.code).filter(Boolean)
            : [];
        this.tracker.track('purchase_start_failed', {
            ...this.properties(),
            error_kind: isValidation ? 'validation' : 'server',
            error_codes: [...new Set(codes)].sort()
        });
    }

    closed(): void {
        if (this.ids && !this.orderCreated) {
            this.tracker.track('purchase_start_abandoned', {
                ...this.properties(),
                submit_attempted: this.submitAttempted
            });
        }
    }

    private properties() {
        return {
            flow: this.flow,
            purchase_attempt_id: this.attemptId,
            ...(this.ids ?? orderStartIds(undefined, undefined))
        };
    }
}
